import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBookContext } from '../../state/BookContext';
import { useFileContext } from '../../state/FileContext';
import {
    decodeHashFragment,
    findHashTargetElement,
    focusHashTarget,
    scrollHashTargetIntoView,
} from '../../utils/hash-scroll.utils';
import { revealCollapsedToggleAncestors } from '../../utils/toggle-beforematch-reveal.utils';

/**
 * Router hash when present; otherwise the window hash.
 * MemoryRouter tests only set `useLocation().hash`. Native `<a href="#id">` only
 * updates `window.location.hash` (React Router 6.21 listens to popstate, not hashchange).
 */
function readHashForAttempt(routerHash: string): string {
    if (typeof window !== 'undefined' && window.location.hash) {
        return window.location.hash;
    }
    return routerHash;
}

/**
 * Client-owned hash-fragment scroll after parsed outlet content is in the DOM.
 * Mount next to RouteChangeListener. All work is in useEffect so SSR is a no-op.
 * Does not call SearchContext.scrollToElement (hash uses block: start; search stays center).
 * Does not key off location.search (filter↔URL rewrites must not re-scroll).
 */
export const HashScrollListener = (): null => {
    const location = useLocation();
    const { fileState } = useFileContext();
    const { bookState } = useBookContext();

    useEffect(() => {
        let cancelled = false;
        let commitFrame: number | null = null;
        let revealFrame: number | null = null;
        let revealTimeout: ReturnType<typeof setTimeout> | null = null;

        const cancelScheduled = (): void => {
            if (commitFrame !== null) {
                cancelAnimationFrame(commitFrame);
                commitFrame = null;
            }
            if (revealFrame !== null) {
                cancelAnimationFrame(revealFrame);
                revealFrame = null;
            }
            if (revealTimeout !== null) {
                clearTimeout(revealTimeout);
                revealTimeout = null;
            }
        };

        const scrollAndFocus = (target: HTMLElement): void => {
            if (cancelled) {
                return;
            }
            scrollHashTargetIntoView(target);
            focusHashTarget(target);
        };

        const attemptScroll = (hash: string): void => {
            cancelScheduled();
            const id = decodeHashFragment(hash);
            if (!id) {
                return;
            }
            commitFrame = requestAnimationFrame(() => {
                commitFrame = null;
                if (cancelled) {
                    return;
                }
                const target = findHashTargetElement(id);
                if (!target) {
                    return;
                }
                const openedToggles = revealCollapsedToggleAncestors(target);
                if (!openedToggles) {
                    scrollAndFocus(target);
                    return;
                }
                revealFrame = requestAnimationFrame(() => {
                    revealFrame = null;
                    revealTimeout = setTimeout(() => {
                        revealTimeout = null;
                        scrollAndFocus(target);
                    }, 0);
                });
            });
        };

        attemptScroll(readHashForAttempt(location.hash));

        const onHashChange = (): void => {
            if (cancelled) {
                return;
            }
            // Router state is stale for native fragment clicks; always read the window hash.
            attemptScroll(window.location.hash);
        };
        window.addEventListener('hashchange', onHashChange);

        return () => {
            cancelled = true;
            cancelScheduled();
            window.removeEventListener('hashchange', onHashChange);
        };
    }, [location.hash, location.pathname, fileState.content, bookState.bookContent]);

    return null;
};
